import { useEffect } from 'react';
import { apiModules, supabase } from '../../api/apiService';
import { Recipe } from '../../api/Recipe';
import { Author } from '../../api/Author';
import {
  logRealtimeRecipeError,
  mergeUpdatedAuthorIntoRecipes,
  prependRecipeIfMissing,
  removeRecipeById,
  replaceRecipeById,
} from './recipeRealtimeSync.helpers';

interface UseRecipeRealtimeSyncOptions {
  setAllRecipes: React.Dispatch<React.SetStateAction<Recipe[]>>;
}

export const useRecipeRealtimeSync = ({
  setAllRecipes,
}: UseRecipeRealtimeSyncOptions): void => {
  useEffect(() => {
    const channel = supabase
      .channel('home_recipes_channel')
      .on<Recipe>(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'recipes' },
        async (payload) => {
          const newRecipeData = payload.new as Recipe;
          const oldRecipeData = payload.old as Recipe;

          switch (payload.eventType) {
            case 'INSERT':
              if (newRecipeData.id) {
                try {
                  const completeRecipe = await apiModules.recipes.getRecipeById(newRecipeData.id);
                  setAllRecipes((previousRecipes) =>
                    prependRecipeIfMissing(previousRecipes, completeRecipe)
                  );
                } catch (error) {
                  logRealtimeRecipeError('INSERT', error);
                }
              }
              break;
            case 'UPDATE':
              if (newRecipeData.id) {
                try {
                  const completeUpdatedRecipe = await apiModules.recipes.getRecipeById(newRecipeData.id);
                  setAllRecipes((previousRecipes) =>
                    replaceRecipeById(previousRecipes, newRecipeData.id, completeUpdatedRecipe)
                  );
                } catch (error) {
                  logRealtimeRecipeError('UPDATE', error);
                }
              }
              break;
            case 'DELETE':
              setAllRecipes((previousRecipes) => removeRecipeById(previousRecipes, oldRecipeData.id));
              break;
            default:
              break;
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [setAllRecipes]);

  useEffect(() => {
    const channel = supabase
      .channel('home_authors_channel')
      .on<Author>(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'authors' },
        async (payload) => {
          const updatedAuthorData = payload.new as Author;

          setAllRecipes((previousRecipes) => mergeUpdatedAuthorIntoRecipes(previousRecipes, updatedAuthorData));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [setAllRecipes]);
};

export default useRecipeRealtimeSync;
